import { z } from "zod";
import { sessionStore, useStore } from "../../../lib/store";

// Convert an interrupted RPPS session at the storage boundary.
const storedRppsSession = z.object({ hcpIdentifier: z.string() }).passthrough();
const storedHealthProSession = z
  .object({ healthProReceipt: z.string().nullable() })
  .passthrough();
const OnboardingFlowSchema = z.preprocess(
  (value) => {
    const healthProSession = storedHealthProSession.safeParse(value);
    const normalized = healthProSession.success
      ? (() => {
          const { healthProReceipt, ...rest } = healthProSession.data;
          return {
            ...rest,
            hcpDirectorySelection:
              healthProReceipt === null
                ? null
                : { registry: "health_pro", receipt: healthProReceipt },
          };
        })()
      : value;
    const rppsSession = storedRppsSession.safeParse(normalized);
    if (!rppsSession.success) return normalized;
    const { hcpIdentifier, ...rest } = rppsSession.data;
    return {
      ...rest,
      registryIdentity:
        hcpIdentifier.trim() === ""
          ? null
          : { registry: "rpps", identifier: hcpIdentifier.trim() },
    };
  },
  z.object({
    // The account exists (Firebase + PG). Set by the email-code slide the moment
    // it creates one, by the password signup at finalize, and by `initialize()`
    // for anyone who arrives already authenticated.
    accountCreated: z.boolean(),
    // The last slide's Continue was pressed. What a remount reads to land back on
    // the last question with its finalize resumed. `accountCreated` used to
    // carry this too, and could while the account was only ever created last; the
    // email code creates it three slides earlier, so the two had to come apart.
    // Defaulted for the deploy-day reason the fields below carry.
    finalizeStarted: z.boolean().default(false),
    // This signup went through an email code. Distinguishes it from an SSO or
    // returning arrival, which is also authenticated and also has an account, but
    // has a different set of slides left to walk.
    otpVerified: z.boolean().default(false),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    country: z.string(),
    profession: z.string().nullable(),
    specialty: z.string(),
    // The profession a confirmed registry record supplied, kept separate from
    // manual profession changes so country changes can discard only sourced data.
    registryProfession: z.string().default(""),
    // Confirmed or manually entered identifier, scoped to its national registry.
    registryIdentity: z
      .object({
        registry: z.enum(["rpps", "npi", "inami", "big", "om", "stps"]),
        identifier: z.string(),
      })
      .nullable()
      .default(null),
    hcpDirectorySelection: z
      .object({
        registry: z.enum(["health_pro", "big", "om", "reps", "stps"]),
        receipt: z.string(),
      })
      .nullable()
      .default(null)
      .catch(null),
    // The specialty the RPPS record named, kept apart from `specialty` (which any
    // manual tap also writes). It is what lets the specialty step drop out of the
    // flow for a practitioner the registry already answered for, and it must stay
    // separate: a practitioner who then declares a profession that does not offer
    // this specialty gets the question back. Empty string = the registry named
    // none, or was never consulted. Defaulted for the deploy-day reason
    // `isHealthcareProfessional` below carries.
    registrySpecialty: z.string().default(""),
    // Back from a confirmed complete profile opens both answers for correction.
    registryProfileMode: z.enum(["confirmed", "editing"]).default("confirmed"),
    // The practitioner confirmed a directory record. This is not identity proof.
    registryVerified: z.boolean().default(false),
    // Where the practitioner works, as the RPPS record spells it (the structure
    // name of their main activity). Displayed on the desktop identity card and
    // persisted to the user's `employer` in the finalize PATCH. Empty string =
    // unknown. Defaulted for the same deploy-day reason.
    employer: z.string().default(""),
    // "Accept & continue" on the intro slide certifies the user is a healthcare
    // professional. There is no `false`: declining ends the signup signed out.
    // `null` until they answer. A session stored with `false` before this rule
    // fails the schema and restarts the whole flow, on purpose: that user
    // declined and must face the gate again.
    // Defaulted (unlike its siblings) so a session that started before this field
    // existed still parses and resumes, instead of failing the schema and wiping
    // the whole flow mid-onboarding on deploy day.
    isHealthcareProfessional: z.literal(true).nullable().default(null),
    // Where this run came in. "desktop-gate": the desktop auth gate's modal took
    // the code and the HCP certification, so the run has no code or intro slide.
    // "carousel": every other run, which starts inside the carousel. Persisted
    // because the proof the gate leaves is in memory, and a reload after the
    // account exists must still know the intro was never part of the run.
    // Defaulted for the deploy-day reason `isHealthcareProfessional` carries.
    entrance: z.enum(["carousel", "desktop-gate"]).default("carousel"),
    // The Specialty whose Practice Profiles offer left COLD — with no slide to fly
    // across, the RPPS fast track having landed on the practice slide at once, so
    // the wait is one the practitioner watches. It lives here rather than in React
    // state because the fast track writes it in the same gesture as the Specialty,
    // and this store's write is the one that commits first. Empty string = none.
    // Defaulted for the deploy-day reason `isHealthcareProfessional` carries.
    practiceOfferStartedColdFor: z.string().default(""),
    // The Specialty whose Practice Profiles offer was GIVEN UP ON — the deadline
    // passed while it was in flight, or the request came back with nothing. Here
    // rather than in React state because the verdict has to outlive a REMOUNT of
    // the flow hook, and one happens inside the run it measures: the Firebase
    // auth-state change at finalize. React state cleared there, the failure
    // branch — which emits with no clock — counted one abandonment twice.
    // Empty string = none. Defaulted for the deploy-day reason above.
    practiceOfferAbandonedFor: z.string().default(""),
    // The Specialty whose practice question the practitioner put off with
    // "Later". Finalize then saves nothing, so the account stays unasked and the
    // app asks again before Explore. Tied to the Specialty like `practice`: a
    // practitioner who goes back and declares another one is asked about it.
    // Defaulted and caught for the deploy-day reason above.
    practiceSkippedFor: z.string().nullable().default(null).catch(null),
    // The Practice Profiles slide's answer, persisted after the account exists.
    //
    // It carries the Specialty it is an answer ABOUT, and that is not decoration:
    // the two halves used to be free-floating fields, so a practitioner who went
    // back and declared a different Specialty kept the practices of the first one —
    // shown checked under a catalogue they do not belong to, and written by the
    // finalize PUT. An answer is only ever an answer to one catalogue's question,
    // and a reader that finds a different Specialty here reads UNTOUCHED.
    //
    // `profileUids: null` means UNTOUCHED, and it is not the same as `[]`:
    // untouched keeps whatever the evidence lookup checked, while an empty array is
    // a practitioner who unchecked everything. Collapsing the two would silently
    // turn "I did nothing" into "none of these" — and throw away a correct
    // recognition on the way. `[]` no longer LEAVES the slide in the arm (one
    // practice is required), but it is a state the screen passes through on the way
    // to a corrected answer, and this store is what it passes through.
    //
    // `disambiguation` is the axis's answer: the facet VALUES the practitioner
    // works across (e.g. ["poultry", "swine"]). Null until answered — and while it
    // is null, or emptied back out, on a Specialty that asks, Continue stays
    // blocked (`canLeavePracticeAxisQuestion`).
    //
    // `.catch(null)` reads an unparseable answer as NO answer, and it guards the
    // rest of this store rather than this field: the whole flow is one JSON blob
    // under one key, so without it a `practice` shape this schema cannot read —
    // an answer written by an older tab, a hand-edited session — takes the name,
    // the email and the country down with it, mid-signup. An unreadable answer to
    // one question is worth exactly that question.
    // Where the signup stands on the partner objection slide
    // (docs/partner-objection.md, O1), the last slide. Every stage but `none`
    // resumes a remount (a reload, a killed app) on that slide: the account
    // already exists, so a remount would otherwise land on the country. The
    // two `saved_*` stages name the choice the API holds: Continue with the
    // same box only finalizes, and a box changed since saves again.
    // Defaulted for the deploy-day reason above, and caught like `practice`:
    // a stage this schema cannot read (an earlier build wrote `confirmed`)
    // must not take the name, the email and the account down with it.
    partnerObjectionStage: z
      .enum(["none", "shown", "saved_sharing", "saved_objected"])
      .default("none")
      .catch("none"),
    // The objection box. Never checked when the slide first opens (INV-06);
    // kept here so a remount does not reset it.
    partnerObjected: z.boolean().default(false),
    practice: z
      .object({
        specialtyUid: z.string(),
        profileUids: z.array(z.string()).nullable(),
        disambiguation: z.array(z.string()).nullable(),
        declaration: z.string().nullable().default(null),
      })
      .nullable()
      .default(null)
      .catch(null),
  })
);

export type OnboardingFlow = z.infer<typeof OnboardingFlowSchema>;

const initialOnboardingFlow: OnboardingFlow = {
  accountCreated: false,
  finalizeStarted: false,
  otpVerified: false,
  firstName: "",
  lastName: "",
  email: "",
  country: "",
  profession: null,
  specialty: "",
  registryProfession: "",
  registryIdentity: null,
  hcpDirectorySelection: null,
  registrySpecialty: "",
  registryProfileMode: "confirmed",
  registryVerified: false,
  employer: "",
  isHealthcareProfessional: null,
  entrance: "carousel",
  practiceOfferStartedColdFor: "",
  practiceOfferAbandonedFor: "",
  practiceSkippedFor: null,
  partnerObjectionStage: "none",
  partnerObjected: false,
  practice: null,
};

export const onboardingFlowStore = sessionStore(
  "onboarding-flow",
  initialOnboardingFlow,
  OnboardingFlowSchema
);

// Persist the normalized session once so subsequent reads use the current shape.
onboardingFlowStore.set(onboardingFlowStore.get());

export function useOnboardingFlowStore() {
  return useStore(onboardingFlowStore);
}

export function updateOnboardingFlow(partial: Partial<OnboardingFlow>) {
  onboardingFlowStore.set((prev) => ({ ...prev, ...partial }));
}

export function resetOnboardingFlow() {
  onboardingFlowStore.set(initialOnboardingFlow);
}
